import type { HTMLComponent } from "@commercekit/react";
import type { FC } from "react";
import { useMemo } from "react";
import { usePrismicLabels } from "~/hooks/usePrismicLabel";
import type { ImageField, LinkField } from "@prismicio/client";
import { PrismicImage } from "@prismicio/react";
import { useLocation } from "@remix-run/react";
import { useLocalePath, useLocale } from "@commercekit/remix/lib/hooks/useLocale";
import WvLink from "~/components/atoms/WvLink";
import { useGlobalCtx } from "~/store/globalState";
import { StarRating } from "~/components/reviews";
import {
  formatRating,
  formatSteppedReviewCount,
} from "~/components/reviews/utils";

const Container: HTMLComponent = ({ children }) => (
  <div className="container h-100">{children}</div>
);

const HeaderDesktopShoppingCart: FC<{
  logo: ImageField;
  logoLink: LinkField;
  headerRating?: { rating: number; reviewCount: number } | null;
}> = ({ logo, logoLink: _logoLink, headerRating }) => {
  const { $pt } = usePrismicLabels();

  const currentLocation = useLocation();

  const localePath = useLocalePath();
  const { locale } = useLocale();
  const steppedReviewCount = headerRating
    ? formatSteppedReviewCount(headerRating.reviewCount, locale)
    : "0+";

  const isCartLayout = useMemo(
    () => currentLocation.pathname.split("/")?.includes("cart"),
    [currentLocation]
  );

  const isCheckoutLayout = useMemo(
    () => currentLocation.pathname.split("/")?.includes("checkout"),
    [currentLocation]
  );

  const isConfirmationLayout = useMemo(
    () =>
      currentLocation.pathname.split("/")?.includes("thank-you") &&
      !currentLocation.pathname.split("/")?.includes("customer"),
    [currentLocation]
  );

  return (
    <Container>
      <div className="row no-gutters align-items-center py-lg-3">
        <div className="col col-lg-auto d-flex flex-column flex-lg-row justify-content-center align-items-center justify-content-lg-start">
          <WvLink
            to={localePath("/")}
            className="d-flex align-items-center justify-content-center px-1"
          >
            <span className="wvheader__logo d-flex flex-column align-items-center justify-content-center">
              <PrismicImage field={logo} height={41} width={203} />
              <span className="wvheader__slogan d-none d-lg-block mt-2">
                {$pt("header_slogan")}
              </span>
            </span>
          </WvLink>
        </div>

        <div className="col">
          <div className="wvheader__steps d-lg-flex justify-content-lg-center">
            <div
              className={`${
                isCartLayout ? "d-flex" : "d-none d-lg-flex"
              } flex-lg-column align-items-center justify-content-end justify-content-md-center justify-content-lg-end px-lg-4`}
            >
              <div className="wvheader__step text-center d-lg-flex order-lg-1 active">
                <div className="wvheader__step-hexagon d-flex align-items-center justify-content-center active">
                  1
                </div>
              </div>
              <div className="wvheader__step-text ml-4 ml-lg-0 pl-2 pl-lg-0 pb-lg-2 d-flex order-lg-0">
                {$pt("checkout_label_step_one")}
              </div>
            </div>

            <div
              className={`${
                isCheckoutLayout ? "d-flex" : "d-none d-lg-flex"
              } flex-lg-column align-items-center justify-content-end justify-content-md-center justify-content-lg-end px-lg-4`}
            >
              <div
                className={`${
                  isCheckoutLayout || isConfirmationLayout ? "active" : ""
                } wvheader__step text-center d-lg-flex order-lg-1`}
              >
                <div
                  className={`${
                    isCheckoutLayout || isConfirmationLayout ? "active" : ""
                  } wvheader__step-hexagon d-flex align-items-center justify-content-center`}
                >
                  2
                </div>
              </div>
              <div className="wvheader__step-text ml-4 ml-lg-0 pl-2 pl-lg-0 pb-lg-2 d-flex order-lg-0">
                {$pt("checkout_label_step_two")}
              </div>
            </div>

            <div
              className={`${
                isConfirmationLayout ? "d-flex" : "d-none d-lg-flex"
              } d-none d-lg-flex flex-lg-column align-items-center justify-content-end justify-content-md-center justify-content-lg-end px-lg-4`}
            >
              <div
                className={`${
                  isConfirmationLayout ? "active" : ""
                } wvheader__step text-center d-lg-flex order-lg-1`}
              >
                <div
                  className={`${
                    isConfirmationLayout ? "active" : ""
                  } wvheader__step-hexagon d-flex align-items-center justify-content-center`}
                >
                  3
                </div>
              </div>
              <div className="wvheader__step-text ml-4 ml-lg-0 pl-2 pl-lg-0 pb-lg-2 d-flex order-lg-0">
                {$pt("checkout_label_step_three")}
              </div>
            </div>
          </div>
        </div>

        <div className="col-auto d-flex justify-content-end">
          <div className="wvheader__rating d-none d-lg-block ml-3">
            {headerRating && headerRating.rating > 0 ? (
              <WvLink to={localePath("/reviews/")}>
                <div className="wvheader__rating-stars">
                  <StarRating rating={headerRating.rating} size="lg" />
                </div>
                <div className="mt-1 wvheader__rating-count">
                  {$pt("header_reviews_dynamic_label", {rating: formatRating(headerRating.rating, locale || "nl-nl"), totalReviews: steppedReviewCount})}
                </div>
              </WvLink>
            ) : (
              <>
            <div className="wvheader__rating-stars">
              <i data-icon-before=""></i>
              <i data-icon-before=""></i>
              <i data-icon-before=""></i>
              <i data-icon-before=""></i>
              <i data-icon-before=""></i>
            </div>
            <div className="wvheader__rating-count mt-1">
              {$pt("header_review_label")}
            </div>
            {/* </PrismicLink> */}
            </>
            )}
          </div>
        </div>
      </div>
    </Container>
  );
};

const HeaderShoppingCart: FC<{
  logo: ImageField;
  logoLink: LinkField;
  headerRating?: { rating: number; reviewCount: number } | null;
}> = ({ logo, logoLink, headerRating }) => {
  const { isCheckoutHeaderLayout } = useGlobalCtx();
  return (
    <div
      style={{ display: isCheckoutHeaderLayout ? "block" : "none" }}
      className="w-full cart"
    >
      <HeaderDesktopShoppingCart logoLink={logoLink} logo={logo} headerRating={headerRating}/>
    </div>
  );
};

export default HeaderShoppingCart;
